import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import LocationSection from "@/components/LocationSection";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { REVIEW_COUNT } from "@/config/reviews";
import { MapPin } from "lucide-react";
import heroImage from "@/assets/great-falls-paterson-nj-masonry-pro-hero.webp";
import vanImage from "@/assets/paver-contractor-van-center-city-mall-paterson.webp";
import truckImage from "@/assets/paver-contractor-truck-paterson-neighborhood-street.webp";
import edgeImage from "@/assets/paver-patio-edge-restraint-base-detail-paterson-nj.webp";
import samplesImage from "@/assets/paver-patio-pattern-samples-paterson-brick-home.webp";
import reviewImage from "@/assets/paver-patio-review-homeowner-mason-paterson.webp";

const faqs = [
  {
    question: "Can you work on a downtown Paterson lot with no driveway?",
    answer:
      "Yes. We carry material by hand on plenty of downtown jobs. What we need to know first is where the truck can stop, whether pallets can stage on the sidewalk, and how far the walk to the back is. We sort all of that out on the estimate visit so there are no surprises on delivery day.",
  },
  {
    question: "Can pavers match the brick on an older Paterson home?",
    answer:
      "Usually, yes. Clay brick pavers are the closest match, and concrete pavers now come in blends that sit well against old brick. We bring samples and hold them against the house before anything is ordered, since color reads differently outdoors and differently again when wet.",
  },
  {
    question: "Can a paver patio be installed in the winter?",
    answer:
      "We usually install into early January, then start back up around mid-March. A mild winter stretches that window. If you want a patio for spring, calling during the winter beats calling in May, because the calendar fills up fast once the weather turns.",
  },
];

export default function PaverPatioDowntownPaterson() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Patio Downtown Paterson NJ | Masonry Pro Construction</title>
        <meta
          name="description"
          content="Paver patio installation in downtown Paterson, NJ, near the Great Falls and Center City Mall. Proper base prep for tight lots. Free estimates, no trip fee."
        />
        <meta name="author" content="Masonry Pro Construction" />
        <meta
          name="keywords"
          content="masonry contractor, paver patio, retaining wall, stone work, North Jersey, Bergen County, Essex County, Passaic County"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Paver Patio Downtown Paterson NJ | Masonry Pro Construction" />
        <meta
          property="og:description"
          content="Paver patio installation in downtown Paterson, NJ, near the Great Falls and Center City Mall. Proper base prep for tight lots. Free estimates, no trip fee."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@MasonryPro" />
        <meta name="twitter:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:title" content="Paver Patio Downtown Paterson NJ | Masonry Pro Construction" />
        <meta
          name="twitter:description"
          content="Paver patio installation in downtown Paterson, NJ, near the Great Falls and Center City Mall. Proper base prep for tight lots. Free estimates, no trip fee."
        />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            generateServiceSchema({
              name: "Paver Patio Installation in Downtown Paterson, NJ",
              description:
                "Working on a tight downtown lot? We plan access and drainage first, then build on a proper compacted base so the patio stays level.",
              url: "/service-areas/passaic-county/paver-patio-downtown-paterson",
              image: "https://masonry-pro.com/og/paver-patios.jpg",
            })
          )}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative">
            <img
              src={heroImage}
              alt="Great Falls of the Passaic River in Paterson, NJ"
              width={1200}
              height={675}
              fetchPriority="high"
              decoding="async"
              className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 to-black/65 flex items-center justify-center">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                    <span style={{ color: '#d4a853', fontWeight: 500 }}>Paterson, Passaic County, NJ</span>
                  </div>
                  <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                    Paver Patio Installation in{" "}
                    <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Downtown Paterson, NJ</span>
                  </h1>
                  <p style={{ color: '#c4a24e', WebkitTextFillColor: '#c4a24e', fontSize: '0.95rem', fontWeight: 500, letterSpacing: '0.025em', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                    <Link to="/paving-services/paver-patios" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Paver Patios</Link> | <Link to="/service-areas/passaic-county" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Passaic County</Link> | Free Estimates, No Trip Fees
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  Masonry Pro Construction is a family-owned masonry contractor based in Totowa. We install paver patios, walkways, and retaining walls across North Jersey. Downtown Paterson is a straight run down Route 80 from our shop, and we work there often.
                </p>
              </div>
            </div>
          </section>

          <LocationSection
            heading="Paver Patios Between the"
            headingHighlight="Great Falls and Main Street"
            imageSrc={vanImage}
            imageAlt="Masonry contractor van on Main Street near Center City Mall in downtown Paterson, NJ"
            imageSide="right"
            altBackground={false}
          >
            <p>
              Downtown Paterson sits along the Passaic River on the west side of the city. Main Street and Market Street run through the middle of it. Center City Mall anchors one end, the county courthouse sits a block over on Hamilton Street, and the Great Falls is a short walk up the river from all of it.
            </p>
            <p>
              Our shop is in Totowa. Take Route 80 east and you are downtown in a few minutes.
            </p>
            <p>
              We say that for a practical reason, not a sales one. Patio work runs on deliveries, cuts, and small decisions made on the spot. When the shop is close, a short pallet or a change to a border gets handled that afternoon. When it is not, you wait.
            </p>
            <p>
              Most of the homes around here are older. Brick rowhomes, two-family houses, narrow lots that run back from the street. The yards are smaller than what you find out in the suburbs, and the space behind the house often matters more because there is less of it.
            </p>
            <p>
              We work across{" "}
              <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic County</Link>
              , and downtown is one of the areas we get called into most.
            </p>
          </LocationSection>

          <LocationSection
            heading="Working on a Downtown Lot Is a"
            headingHighlight="Different Job"
            imageSrc={truckImage}
            imageAlt="Paver pallets staged on the sidewalk beside a two-family home on a Paterson street"
            imageSide="left"
            altBackground={true}
          >
            <p>
              Here is the part most contractors will not bring up until the truck is already on your street.
            </p>
            <p>
              Downtown lots are tight. Some houses have no driveway at all. Cars line both sides of the road. There may be twenty feet of walkway between the curb and your back yard, and every paver has to travel it by hand.
            </p>
            <p>
              None of that is a problem. It is just something to plan instead of discover.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Where the truck can legally stop and for how long</li>
              <li>Whether pallets can sit on the sidewalk or need to go to the back</li>
              <li>How far the material has to be carried, and whether a wheelbarrow fits the path</li>
              <li>Whether a gate, fence, or shared alley has to be worked around</li>
              <li>Where the spoil from the excavation goes while the job runs</li>
            </ul>
            <p>
              We walk all of that on the estimate visit. It affects the price, and we would rather tell you that up front than surprise you later.
            </p>
            <p>
              Small yards also change the design. A courtyard patio behind a rowhome is not a scaled-down suburban patio. Layout matters more when there is less room, and so does where the steps land and how the patio meets the existing walk.
            </p>
          </LocationSection>

          <LocationSection
            heading="What a Downtown Patio"
            headingHighlight="Has to Survive"
            imageSrc={edgeImage}
            imageAlt="Edge restraint and bedding sand along the edge of a paver patio in Paterson, NJ"
            imageSide="right"
            altBackground={false}
          >
            <p>
              Standing water sitting against pavers through a North Jersey winter is asking for trouble. That is why we grade every base to move water off the patio and away from the house.
            </p>
            <p>
              A paver patio is layers, and most of them are buried. We dig down first. Base depth usually runs around 6 to 10 inches, depending on your soil and what the patio has to carry. Compacted gravel goes in, then a bedding layer of sand, then the pavers. Polymeric sand gets swept into the joints and compacted at the end.
            </p>
            <p>
              Get the slope and the base right and the pavers on top do their job for years without drama.
            </p>
            <p>
              Water freezes, expands, and pushes. Do that enough times over one winter and a patio built on a thin base starts to move. Corners lift. Joints open.
            </p>
            <p>
              Downtown adds one more wrinkle. Older properties often have fences, walls, and neighboring structures crowded right up against the yard, so there are fewer directions for water to go. Sorting out where it drains is part of the design, not an afterthought.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Slope is planned away from the house, never toward it</li>
              <li>Edge restraint runs the full perimeter so the field cannot spread</li>
              <li>Polymeric sand in the joints also keeps weeds and ants down</li>
              <li>Damaged sections can be lifted and reset without redoing the patio</li>
            </ul>
            <p>
              A poured slab cracks straight across and you are looking at replacing the whole thing. With pavers we pull the affected section, fix what is underneath, and set the same units back down.
            </p>
          </LocationSection>

          <LocationSection
            heading="Matching Pavers to a"
            headingHighlight="Brick Home"
            imageSrc={samplesImage}
            imageAlt="Paver samples laid against the brick of a Paterson home"
            imageSide="left"
            altBackground={true}
          >
            <p>
              A lot of downtown houses are brick, and brick has opinions about what goes next to it.
            </p>
            <p>
              Concrete pavers are what most homeowners choose. They come in enough colors and textures to sit comfortably against old brick, they hold up to freeze and thaw, and replacement units are easy to find years later. Clay brick pavers are the closer match if you want the patio to read as part of the house. Natural stone gives you a surface where no two pieces are alike.
            </p>
            <p>
              Pattern matters as much as material. Herringbone interlocks, so it holds up well where people actually walk. Running bond keeps a small patio looking calm instead of busy. A contrasting border course frames the whole thing and gives the edge somewhere to land.
            </p>
            <p>
              On a tight lot, the right pattern also cuts waste. Fewer odd cuts around a corner means a cleaner finish and less material bought for nothing.
            </p>
            <p>
              We bring samples and lay them against the house. Color looks different outdoors than it does in a catalog, and different again once it is wet.
            </p>
            <p>
              If you want the longer version of how we build one, it is on our{" "}
              <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patio installation page</Link>
              . For work on the other side of the city, we cover the Stoney Road area too, over by{" "}
              <Link to="/service-areas/passaic-county/paver-patio-pennington-park-paterson" className="text-secondary hover:underline">Pennington Park</Link>
              .
            </p>
          </LocationSection>

          <InlineCTA
            heading="Planning a Patio in Downtown Paterson?"
            highlightedHeading="Let's Walk the Space"
            description="Call or text us directly or scroll down to request your free estimate."
          />

          <LocationSection
            heading="Talk to Us About Your"
            headingHighlight="Downtown Paterson Patio"
            imageSrc={reviewImage}
            imageAlt="Homeowner and mason reviewing a finished paver patio in Paterson, NJ"
            imageSide="right"
            altBackground={false}
          >
            <p>
              Homeowners downtown usually call with the same three questions. Will it fit. Will it drain. What does it run.
            </p>
            <p>
              The first two are easier to answer standing in the yard.
            </p>
            <p>
              Masonry Pro Construction is at 36 Highview Ave in Totowa, New Jersey. Call or text us at (973) 725-2395. Estimates are free and there is no trip fee, so a visit costs you nothing but the time it takes to walk the space with us.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Address: 36 Highview Ave, Totowa, NJ 07512</li>
              <li>Phone: (973) 725-2395 for scheduling, free estimates, or questions</li>
              <li>Service area: Passaic, Bergen, Essex, and Morris counties</li>
              <li>NJ HIC License #13VH06777200</li>
              <li>Biagio has been laying masonry for over 35 years and is a bricklayers union member</li>
            </ul>
            <p>
              This has been a family business for a long time, and we have picked up more than {REVIEW_COUNT}+ five-star Google reviews along the way. Gino has been working alongside his father for over 14 years. The person who walks your yard is the person who runs the job.
            </p>
            <p>
              If a photo is easier than a phone call, send one. A picture of the space with a rough idea of the dimensions is usually enough for us to give you a ballpark before anyone schedules anything.
            </p>
          </LocationSection>

          {/* FAQ Cards */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions About Paver Patios in{" "}
                <span className="text-gradient-gold">Downtown Paterson</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <CTASection
            serviceName="Downtown Paterson Paver Patio"
            heading="Get a Free Estimate on Your"
            highlightedHeading="Downtown Paterson Paver Patio"
          />
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-2">
                <p className="text-xl font-bold">Masonry Pro Construction</p>
                <p className="text-muted-foreground">36 Highview Ave, Totowa, NJ 07512</p>
                <a href="tel:9737252395" className="text-secondary hover:underline text-lg font-semibold block">
                  (973) 725-2395
                </a>
                <p className="text-sm text-muted-foreground italic mt-4">
                  NJ HIC License{" "}
                  <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
